// The title screen's background (docs/plans/2026-10-18-dharna-144.md, section 5): Connaught Place as the operation
// map on a dispatcher's desk, cut from the real district map (tools/make-title-map.mjs, served as /title-map.json).
// The sheet is baked once per scale: cream paper, roads in solid ink with their lane dashes, blocks in dark ink-grey,
// trees as blots, the metro lines dashed, everything worn by the print. Over it, live: the crowd in metro yellow walks
// the actual road graph in to the Inner Circle and fills it, held at two rings of barricade-blue police lines that
// swing open and shut; boxed labels; and signposts at the edge to the places round it, at their real distances.
import { FONT, INK, MAP, box } from './ui';

interface Line { c: string; w: number; n?: string; o?: 1; l: number[] }
export interface TitleData {
  name: string; attribution: string; mpt: number; w: number; h: number;
  centre: [number, number]; rings: { inner: number; middle: number; outer: number };
  styles: string[]; roads: Line[]; buildings: { s: number; n?: string; o: number[] }[]; trees: [number, number][];
  parks: [number, number, number][]; stations: { n: string; x: number; y: number }[]; metro: { n: string; l: number[] }[];
  places: { n: string; x: number; y: number; km: number }[];
  minimap: { w: number; h: number; roads: number[][]; metro: number[][]; cut: number[]; at: number[] };
}
interface Node { x: number; y: number; d: number; to: number[]; w: number[] }
/**
 * A police line across a road. `ux, uy` points along the road towards the Inner Circle, the side its police stand on;
 * `push` (0..1) is how far the crowd has shoved it back towards them, run by `phase` and its timer `pt`.
 */
interface Bar {
  key: string; t: number; x: number; y: number; ang: number; half: number; open: boolean; until: number; lift: number;
  ux: number; uy: number; push: number; phase: 'rest' | 'surge' | 'held' | 'back'; pt: number;
}
interface Dot { a: number; b: number; t: number; lane: number; v: number; rank: number; held?: Bar }
interface Rect { x0: number; y0: number; x1: number; y1: number }

/**
 * The camera's tour of the protest sites: it holds on one, then slides across the sheet to the next, which is stamped
 * with its name. Times in seconds.
 */
const DWELL_S = 11, MOVE_S = 2.8, SITE_R: Record<string, number> = { 'Jantar Mantar': 40, 'Parliament House': 46, 'Mandi House': 40 }; // about Connaught Place's own framing
interface Site { n: string; sub: string; x: number; y: number; r: number; crowd: { x: number; y: number; ph: number }[] }
const SPEED_MPS = 10; // the crowd's walk, sped up so the screen is never still
// the crowd's push at a police line: how far it shoves the line and its police back (PUSH_M, but never under PUSH_DOTS
// dots on screen, or it would not read), how long the surge, the hold and the shove back take (s), and the rest between
const PUSH_M = 5, PUSH_DOTS = 3.2, SURGE_S = 1.1, HELD_S = [0.6, 1.4], BACK_S = 1.6, REST_S = [5, 14];
const MAJOR = new Set(['trunk', 'primary', 'secondary', 'tertiary']);

/** The operation map of Connaught Place behind the title. World units are the cut's tiles (data.mpt metres each). */
export class TitleMap {
  private g: CanvasRenderingContext2D;
  private W = 0; private H = 0; private dpr = 1;
  private D: TitleData | null = null;
  private nodes: Node[] = []; private len = new Map<string, number>(); private barAt = new Map<string, Bar>();
  private bars: Bar[] = []; private dots: Dot[] = []; private held: { x: number; y: number }[] = [];
  /** how far a push shoves a line, in metres: PUSH_M, or more at a scale where that is under PUSH_DOTS dots */
  private pushM = PUSH_M;
  private bake: { c: HTMLCanvasElement; s: number } | null = null;
  private sprites = new Map<string, { c: HTMLCanvasElement; r: number }>();
  private last = 0; private seed = 144; private clock = 0;
  private sites: Site[] = [];
  private tour = { i: 0, from: 0, t: 0, mt: 0, moving: false };
  private slam: { site: Site; t: number } | null = null;
  /** the tour's sounds, if anyone listens: the camera sliding off, and a place's name stamped on arrival */
  sfx: (id: 'slide' | 'stamp') => void = () => {};
  /** 0 paused, 1 walking, 3 fast */
  speed = 1;
  private still = typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * `area` is the screen rectangle the map is composed for (right of the panel on a wide screen); `avoid` the boxes
   * the signposts must keep clear of (the HUD bar, the legend). Both are read on every resize.
   */
  constructor(private canvas: HTMLCanvasElement, private visible: () => boolean, private area: () => Rect, private avoid: () => Rect[], private minimap: HTMLCanvasElement | null = null) {
    this.g = canvas.getContext('2d')!;
    addEventListener('resize', () => this.resize());
    this.resize();
    requestAnimationFrame((t) => this.tick(t));
  }

  /** A seeded random, so the opening frame is the same each visit. */
  private rnd() { this.seed = (this.seed * 16807) % 2147483647; return this.seed / 2147483647; }

  async load(url = '/title-map.json') {
    try { const r = await fetch(url); if (!r.ok) return; this.D = (await r.json()) as TitleData; } catch { return; }
    this.build(); this.resize(); this.drawMinimap();
  }

  private resize() {
    const r = this.canvas.getBoundingClientRect();
    this.dpr = Math.min(2, devicePixelRatio || 1); this.W = r.width || innerWidth; this.H = r.height || innerHeight;
    this.canvas.width = Math.round(this.W * this.dpr); this.canvas.height = Math.round(this.H * this.dpr);
    this.bake = null; this.sprites.clear();
    if (this.still) this.draw();
  }

  // ---------------------------------------------------------------- the road graph, the lines, the crowd

  private build() {
    const D = this.D!, at = new Map<string, number>();
    const node = (x: number, y: number) => {
      const k = `${Math.round(x * 2)},${Math.round(y * 2)}`; let i = at.get(k);
      if (i === undefined) { i = this.nodes.length; this.nodes.push({ x, y, d: Infinity, to: [], w: [] }); at.set(k, i); }
      return i;
    };
    for (const r of D.roads) {
      if (r.c === 'pedestrian') continue;
      let prev = -1;
      for (let i = 0; i < r.l.length; i += 2) {
        const n = node(r.l[i], r.l[i + 1]);
        if (prev >= 0 && prev !== n && !this.nodes[prev].to.includes(n)) {
          const A = this.nodes[prev], B = this.nodes[n], L = Math.hypot(B.x - A.x, B.y - A.y);
          A.to.push(n); A.w.push(r.w); B.to.push(prev); B.w.push(r.w);
          this.len.set(prev + '>' + n, L); this.len.set(n + '>' + prev, L);
        }
        prev = n;
      }
    }
    // the goal: the Inner Circle and everything inside it
    const [cx, cy] = D.centre, R = D.rings, near = (n: Node) => Math.hypot(n.x - cx, n.y - cy);
    const src = this.nodes.map((n, i) => (near(n) <= R.inner + 1.5 ? i : -1)).filter((i) => i >= 0);
    for (const i of src) this.nodes[i].d = 0;
    const open = new Set(src);
    while (open.size) {
      let u = -1; for (const i of open) if (u < 0 || this.nodes[i].d < this.nodes[u].d) u = i;
      open.delete(u);
      const U = this.nodes[u];
      U.to.forEach((v) => { const nd = U.d + this.len.get(u + '>' + v)!; if (nd < this.nodes[v].d) { this.nodes[v].d = nd; open.add(v); } });
    }
    // two rings of police lines: where the roads come in past the Outer Circle, and between the Middle and the Inner
    const metre = 1 / D.mpt;
    for (const ring of [R.outer + 7, (R.inner + R.middle) / 2]) {
      this.nodes.forEach((A, a) => A.to.forEach((b, k) => {
        const B = this.nodes[b];
        if (!(A.d > B.d) || !(near(A) > ring && near(B) <= ring)) return;
        let lo = 0, hi = 1;
        for (let i = 0; i < 16; i++) { const mid = (lo + hi) / 2; if (Math.hypot(A.x + (B.x - A.x) * mid - cx, A.y + (B.y - A.y) * mid - cy) > ring) lo = mid; else hi = mid; }
        const x = A.x + (B.x - A.x) * lo, y = A.y + (B.y - A.y) * lo;
        if (this.bars.some((q) => Math.hypot(q.x - x, q.y - y) < 14 * metre * D.mpt)) return;
        const L = Math.hypot(B.x - A.x, B.y - A.y) || 1;
        const bar: Bar = {
          key: a + '>' + b, t: lo, x, y, ang: Math.atan2(B.y - A.y, B.x - A.x) + Math.PI / 2, half: (A.w[k] / 2 + 2) * metre, open: false, until: 3 + this.rnd() * 12, lift: 0,
          ux: (B.x - A.x) / L, uy: (B.y - A.y) / L, push: 0, phase: 'rest', pt: REST_S[0] * this.rnd() + 1,
        };
        this.bars.push(bar); this.barAt.set(bar.key, bar);
      }));
    }
    // the crowd: spread along the inbound edges, mostly within a kilometre, then a walk of a few seconds so the
    // first frame already has its queues at the lines
    const ins: [number, number][] = [];
    this.nodes.forEach((A, a) => A.to.forEach((b) => { if (Number.isFinite(A.d) && A.d > this.nodes[b].d && A.d < 140) ins.push([a, b]); }));
    if (!ins.length) return;
    for (let i = 0; i < 1300; i++) { const e = ins[Math.floor(this.rnd() * ins.length)]; this.dots.push(this.walker(e[0], e[1], this.rnd())); }
    for (let i = 0; i < 420; i++) this.hold();
    this.buildSites();
    for (let i = 0; i < 240; i++) this.step(0.05);
  }

  private walker(a: number, b: number, t: number): Dot { return { a, b, t, lane: this.rnd() * 2 - 1, v: SPEED_MPS * (0.7 + this.rnd() * 0.6), rank: Math.floor(this.rnd() * 9), held: undefined }; }

  /** A new walker at a node out in the city, on the way in. */
  private respawn(d: Dot) {
    for (let k = 0; k < 40; k++) {
      const a = Math.floor(this.rnd() * this.nodes.length), A = this.nodes[a];
      if (!(A.d >= 60 && A.d <= 150)) continue;
      const b = this.down(a); if (b < 0) continue;
      Object.assign(d, this.walker(a, b, 0)); return;
    }
  }

  /** The next node towards the Inner Circle, a little at random among the ways that get closer. */
  private down(a: number): number {
    const A = this.nodes[a], c = A.to.filter((b) => this.nodes[b].d < A.d);
    if (!c.length) return -1;
    c.sort((p, q) => this.nodes[p].d - this.nodes[q].d);
    return c.length > 1 && this.rnd() < 0.35 ? c[1] : c[0];
  }

  /** One more at the circle: on the Inner Circle's road and pavement mostly, some in the park; the oldest leave. */
  private hold() {
    const D = this.D!, [cx, cy] = D.centre, R = D.rings.inner, a = this.rnd() * Math.PI * 2;
    const r = this.rnd() < 0.72 ? R - 2.2 + this.rnd() * 5.5 : 2 + this.rnd() * (R - 5);
    this.held.push({ x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r });
    if (this.held.length > 560) this.held.shift();
  }

  private tick(now: number) {
    requestAnimationFrame((t) => this.tick(t));
    if (!this.visible() || this.still) { this.last = now; return; }
    const dt = Math.min(0.05, (now - (this.last || now)) / 1000); this.last = now;
    if (this.D) { this.step(dt * this.speed); this.tourStep(dt); }
    this.clock += dt;
    this.draw();
  }

  private step(dt: number) {
    if (!dt) return;
    for (const b of this.bars) { // each line opens for a few seconds, then shuts again; never while it is being pushed
      b.until -= dt;
      if (b.until <= 0) {
        if (!b.open && b.phase !== 'rest') b.until = 1;
        else { b.open = !b.open; b.until = b.open ? 2.5 + this.rnd() * 2.5 : 8 + this.rnd() * 10; }
      }
      b.lift += ((b.open ? 1 : 0) - b.lift) * Math.min(1, dt * 5);
      this.pushStep(b, dt);
    }
    const tile = 1 / this.D!.mpt, gap = 2.4 * tile;
    for (const d of this.dots) {
      const L = this.len.get(d.a + '>' + d.b) || 1;
      let t = d.t + (d.v * tile * dt) / L;
      const bar = this.barAt.get(d.a + '>' + d.b);
      if (bar && !bar.open) {
        // the queue keeps its place behind the line wherever the line is: it surges with a push and is shoved back with it
        const at = bar.t + (bar.push * this.pushM * tile) / L, stop = at - (gap * (1 + d.rank)) / L;
        if ((d.t <= at || d.held === bar) && t > stop) { t = stop; d.held = bar; }
      }
      d.t = t;
      if (d.t < 1) continue;
      if (this.nodes[d.b].d <= 0) { this.hold(); this.respawn(d); continue; }
      const nb = this.down(d.b);
      if (nb < 0) { this.respawn(d); continue; }
      d.t = ((d.t - 1) * L) / (this.len.get(d.b + '>' + nb) || 1); d.a = d.b; d.b = nb; d.held = undefined;
    }
  }

  /** A line's push: at rest, then the crowd surges and shoves it back, it holds there, and the police shove it home. */
  private pushStep(b: Bar, dt: number) {
    b.pt -= dt;
    if (b.phase === 'rest') { if (b.pt <= 0 && !b.open && b.lift < 0.05) { b.phase = 'surge'; b.pt = SURGE_S; } }
    else if (b.phase === 'surge') { b.push += (1 - b.push) * Math.min(1, dt * 3.2); if (b.pt <= 0) { b.phase = 'held'; b.pt = HELD_S[0] + this.rnd() * (HELD_S[1] - HELD_S[0]); } }
    else if (b.phase === 'held') { b.push = Math.min(1, b.push + dt * 0.05); if (b.pt <= 0) { b.phase = 'back'; b.pt = BACK_S; } }
    else { b.push += (0 - b.push) * Math.min(1, dt * 2.4); if (b.pt <= 0 || b.push < 0.02) { b.push = 0; b.phase = 'rest'; b.pt = REST_S[0] + this.rnd() * (REST_S[1] - REST_S[0]); } }
  }

  // ---------------------------------------------------------------- the view and the baked sheet

  /** Tiles to screen: Connaught Place's Outer Circle fills most of the area's height, centred in it. */
  private view() {
    const D = this.D!, A = this.area(), aw = A.x1 - A.x0, ah = A.y1 - A.y0, cam = this.cam(), dia = 2 * cam.r;
    const s = Math.max(Math.min((ah * 0.64) / dia, (aw * 0.8) / dia), this.W / D.w, this.H / D.h); // the sheet covers the screen
    const drift = this.still ? 0 : 1;
    let ox = A.x0 + aw / 2 - cam.x * s + Math.sin(this.clock * 0.05) * 14 * drift;
    let oy = A.y0 + ah / 2 - cam.y * s + Math.cos(this.clock * 0.04) * 10 * drift;
    ox = Math.min(0, Math.max(this.W - D.w * s, ox)); oy = Math.min(0, Math.max(this.H - D.h * s, oy)); // never past the sheet's edge
    return { s, ox, oy };
  }

  // ---------------------------------------------------------------- the tour

  /** Connaught Place, then the places the signposts name that the sheet holds, each with a crowd sat on its roads. */
  private buildSites() {
    const D = this.D!, [cx, cy] = D.centre;
    const near = (x: number, y: number, r: number) => {
      const pts: { x: number; y: number; ph: number }[] = [];
      for (const rd of D.roads) for (let i = 0; i < rd.l.length; i += 2) if (Math.hypot(rd.l[i] - x, rd.l[i + 1] - y) < r) pts.push({ x: rd.l[i], y: rd.l[i + 1], ph: 0 });
      const out: typeof pts = [];
      for (let k = 0; pts.length && k < 260; k++) {
        const p = pts[Math.floor(this.rnd() * pts.length)], a = this.rnd() * Math.PI * 2, d = this.rnd() * 2.2;
        out.push({ x: p.x + Math.cos(a) * d, y: p.y + Math.sin(a) * d, ph: this.rnd() * 6.28 });
      }
      return out;
    };
    this.sites = [{ n: 'CONNAUGHT PLACE', sub: 'THE INNER CIRCLE · RAJIV CHOWK', x: cx, y: cy, r: D.rings.outer, crowd: [] }];
    for (const p of D.places) {
      const r = SITE_R[p.n];
      if (!r || p.x < 0 || p.y < 0 || p.x > D.w || p.y > D.h) continue;
      this.sites.push({ n: p.n.toUpperCase(), sub: `${p.km.toFixed(1)} KM FROM THE INNER CIRCLE`, x: p.x, y: p.y, r, crowd: near(p.x, p.y, r * 0.5) });
    }
  }

  /** Hold, then switch: the camera's next place every DWELL_S, the switch taking MOVE_S. */
  private tourStep(dt: number) {
    const T = this.tour;
    if (this.slam && (this.slam.t += dt) > 3.4) this.slam = null;
    if (this.sites.length < 2) return;
    if (!T.moving) { if ((T.t += dt) > DWELL_S) { T.moving = true; T.mt = 0; T.from = T.i; T.i = (T.i + 1) % this.sites.length; this.sfx('slide'); } return; }
    if ((T.mt += dt / MOVE_S) >= 1) { T.moving = false; T.t = 0; this.slam = { site: this.sites[T.i], t: 0 }; this.sfx('stamp'); }
  }

  /**
   * Where the camera is: a place at rest; in a switch, a slide across the sheet to the next, easing in and out, its
   * framing eased from the one place's to the other's. No zoom out: the sheet is not big enough to look down on.
   */
  private cam(): { x: number; y: number; r: number } {
    const D = this.D!, T = this.tour;
    if (!this.sites.length) return { x: D.centre[0], y: D.centre[1], r: D.rings.outer };
    const b = this.sites[T.i];
    if (!T.moving) return { x: b.x, y: b.y, r: b.r };
    const a = this.sites[T.from], u = Math.max(0, Math.min(1, T.mt)), k = u < 0.5 ? 4 * u * u * u : 1 - (-2 * u + 2) ** 3 / 2; // ease in-out
    return { x: a.x + (b.x - a.x) * k, y: a.y + (b.y - a.y) * k, r: a.r + (b.r - a.r) * k };
  }

  /** The static sheet at s px per tile: paper, parks, blocks, trees, roads, metro, worn by the print. */
  private bakeAt(s: number) {
    const D = this.D!, k = Math.min(s * this.dpr, 4096 / D.w, 4096 / D.h, Math.sqrt(16e6 / (D.w * D.h)));
    const c = document.createElement('canvas'); c.width = Math.ceil(D.w * k); c.height = Math.ceil(D.h * k);
    const g = c.getContext('2d')!;
    g.fillStyle = MAP.paper; g.fillRect(0, 0, c.width, c.height);
    g.scale(k, k);
    const px = 1 / k, m = 1 / D.mpt; // one device px, one metre, in tiles
    g.fillStyle = MAP.park; for (const [y, x0, x1] of D.parks) g.fillRect(x0, y, x1 - x0, 1.02);
    const poly = (o: number[]) => { g.beginPath(); g.moveTo(o[0], o[1]); for (let i = 2; i < o.length; i += 2) g.lineTo(o[i], o[i + 1]); g.closePath(); };
    D.buildings.forEach((b, i) => {
      const style = D.styles[b.s], h = ((i * 2654435761) >>> 0) % 100;
      poly(b.o);
      if (style === 'heritage' || style === 'yantra') { g.fillStyle = MAP.key; g.fill(); return; }
      if (h < 8) { g.fillStyle = '#CDBEA0'; g.fill(); g.lineWidth = 1.5 * px; g.strokeStyle = MAP.key; g.stroke(); return; } // a few light blocks
      g.fillStyle = h < 30 ? MAP.blockAlt : MAP.block; g.fill();
    });
    g.fillStyle = MAP.tree; // trees as ink blots: two overlapping discs, so they read as canopies, not dots
    for (const [x, y] of D.trees) { const r = 3.2 * m; g.beginPath(); g.arc(x, y, r, 0, Math.PI * 2); g.arc(x + r * 0.7, y - r * 0.4, r * 0.75, 0, Math.PI * 2); g.fill(); }
    g.lineCap = 'round'; g.lineJoin = 'round';
    const line = (l: number[]) => { g.beginPath(); g.moveTo(l[0], l[1]); for (let i = 2; i < l.length; i += 2) g.lineTo(l[i], l[i + 1]); };
    g.strokeStyle = MAP.road;
    for (const r of D.roads) if (r.c === 'pedestrian') { g.lineWidth = Math.max(1.2 * m, 1.6 * px); g.setLineDash([1.6 * m, 1.6 * m]); line(r.l); g.stroke(); }
    g.setLineDash([]);
    for (const r of D.roads) if (r.c !== 'pedestrian') { g.lineWidth = Math.max(r.w * m * (MAJOR.has(r.c) ? 1.25 : 1.05), 2.6 * px); line(r.l); g.stroke(); }
    // the paint: a cream edge just inside the wide roads, and dashed lane lines down their middle
    for (const r of D.roads) {
      if (!MAJOR.has(r.c) || r.w < 10) continue;
      g.strokeStyle = MAP.lane; g.lineWidth = Math.max(0.35 * m, 0.9 * px); g.setLineDash([2.4 * m, 2.6 * m]); g.lineCap = 'butt'; line(r.l); g.stroke();
    }
    g.setLineDash([]); g.lineCap = 'round';
    // the metro: dashed ink with a cream core, as the legend draws it
    for (const ml of D.metro) {
      g.strokeStyle = MAP.road; g.lineWidth = Math.max(3.2 * m, 3 * px); g.setLineDash([9 * m, 5 * m]); line(ml.l); g.stroke();
    }
    g.setLineDash([]);
    // stations: an ink disc with a cream ring and an M
    for (const st of D.stations) {
      const r = Math.max(9 * m, 7 * px);
      g.beginPath(); g.arc(st.x, st.y, r, 0, Math.PI * 2); g.fillStyle = MAP.key; g.fill();
      g.lineWidth = r * 0.22; g.strokeStyle = MAP.lane; g.beginPath(); g.arc(st.x, st.y, r * 0.72, 0, Math.PI * 2); g.stroke();
    }
    // worn print: cream specks knocked out of the ink, ink specks on the paper, a few scuffs
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.fillStyle = g.createPattern(wear(), 'repeat')!; g.fillRect(0, 0, c.width, c.height);
    this.bake = { c, s };
  }

  /**
   * A glyph as a sprite, so a thousand of them cost a thousand image copies: the crowd's is metro yellow with an ink
   * centre, the police's barricade blue with a newsprint one (a cap's badge), both ringed in ink.
   */
  private dotSprite(r: number, fill: string = INK.metro, centre: string = INK.asphalt) {
    const key = fill + centre, have = this.sprites.get(key);
    if (have && Math.abs(have.r - r) < 0.01) return have.c;
    const d = this.dpr, c = document.createElement('canvas'), n = Math.ceil((r + 1.5) * 2 * d);
    c.width = c.height = n;
    const g = c.getContext('2d')!; g.scale(d, d);
    const o = n / d / 2;
    g.beginPath(); g.arc(o, o, r, 0, Math.PI * 2); g.fillStyle = fill; g.fill();
    g.lineWidth = Math.max(1, r * 0.34); g.strokeStyle = INK.asphalt; g.stroke();
    g.beginPath(); g.arc(o, o, Math.max(0.9, r * 0.32), 0, Math.PI * 2); g.fillStyle = centre; g.fill();
    this.sprites.set(key, { c, r });
    return c;
  }

  private draw() {
    const g = this.g;
    g.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    g.fillStyle = MAP.paper; g.fillRect(0, 0, this.W, this.H);
    if (!this.D) return;
    const D = this.D, { s, ox, oy } = this.view(), X = (x: number) => ox + x * s, Y = (y: number) => oy + y * s;
    // re-baked only on the ground: in a switch the sheet it has is stretched (a re-bake a frame would stall it)
    if (!this.bake || (!this.tour.moving && Math.abs(this.bake.s - s) / s > 0.02)) this.bakeAt(s);
    g.imageSmoothingEnabled = true; g.imageSmoothingQuality = 'high';
    g.drawImage(this.bake!.c, ox, oy, D.w * s, D.h * s);
    const m = s / D.mpt, r = Math.max(3.4, Math.min(6.5, m * 5)), spr = this.dotSprite(r), half = spr.width / this.dpr / 2;
    const dot = (x: number, y: number) => { if (x > -8 && y > -8 && x < this.W + 8 && y < this.H + 8) g.drawImage(spr, x - half, y - half, half * 2, half * 2); };
    for (const p of this.held) dot(X(p.x), Y(p.y));
    for (const site of this.sites) for (const c of site.crowd) dot(X(c.x + Math.sin(this.clock * 0.7 + c.ph) * 0.35), Y(c.y + Math.cos(this.clock * 0.6 + c.ph) * 0.35)); // sat in
    for (const d of this.dots) {
      const A = this.nodes[d.a], B = this.nodes[d.b], L = this.len.get(d.a + '>' + d.b) || 1;
      const nx = -(B.y - A.y) / L, ny = (B.x - A.x) / L, w = d.lane * 2.6 / D.mpt;
      dot(X(A.x + (B.x - A.x) * d.t + nx * w), Y(A.y + (B.y - A.y) * d.t + ny * w));
    }
    // the police behind each line, laid out in dots (metres are a few pixels at this scale): a front row across the bar,
    // a shorter one behind, pushed back with it, and stepping to the kerb when it swings open
    const cop = this.dotSprite(r, INK.barricade, INK.newsprint), th = Math.max(8, Math.min(13, m * 8));
    this.pushM = Math.max(PUSH_M, (PUSH_DOTS * 2 * r) / m);
    for (const b of this.bars) {
      const hw = Math.max(15, b.half * s * 1.15), shove = b.push * this.pushM * m, x0 = X(b.x), y0 = Y(b.y), px = -b.uy, py = b.ux;
      const n = Math.max(3, Math.min(7, Math.floor((hw * 2) / (r * 2.6))));
      [n, Math.max(2, n - 2)].forEach((k, row) => {
        for (let i = 0; i < k; i++) {
          const spread = -hw + r + ((hw * 2 - r * 2) * (i + 0.5 + row * 0.25)) / k;
          const across = spread + (hw + r * 1.8 + row * r * 2.2 - spread) * b.lift;
          const back = th / 2 + r * 1.5 + row * r * 2.3 + shove + Math.sin(this.clock * 2.1 + i * 1.7 + row) * r * 0.18;
          const x = x0 + b.ux * back + px * across, y = y0 + b.uy * back + py * across;
          if (x > -8 && y > -8 && x < this.W + 8 && y < this.H + 8) g.drawImage(cop, x - half, y - half, half * 2, half * 2);
        }
      });
    }
    // the police lines: a thick blue bar across the road that swings aside on one kerb to open, shoved back by a push
    for (const b of this.bars) {
      const off = b.push * this.pushM * m, x = X(b.x) + b.ux * off, y = Y(b.y) + b.uy * off, hw = Math.max(15, b.half * s * 1.15);
      g.save(); g.translate(x, y); g.rotate(b.ang); g.translate(-hw, 0); g.rotate(-b.lift * 1.3);
      g.fillStyle = INK.asphalt; g.fillRect(3, -th / 2 + 3, hw * 2, th);
      g.fillStyle = INK.barricade; g.fillRect(0, -th / 2, hw * 2, th);
      g.restore();
    }
    this.labels(s, X, Y);
    if (this.slam) this.drawSlam(this.slam);
  }

  /** The place the camera lands on, stamped: its name big, and how far it is from the Inner Circle. */
  private drawSlam(sl: { site: Site; t: number }) {
    const g = this.g, A = this.area(), t = sl.t, a = t < 2.9 ? 1 : Math.max(0, 1 - (t - 2.9) / 0.5);
    const k = t < 0.16 ? 1.3 - (t / 0.16) * 0.3 : 1; // it lands: a stamp coming down
    // just above whatever sits on the foot of the screen under it (the next-episode strip, the legend)
    const cx = (A.x0 + A.x1) / 2, under = this.avoid().filter((q) => q.y0 > this.H * 0.5 && q.x0 < cx + 160 && q.x1 > cx - 160);
    const cy = Math.min(A.y1 - Math.min(150, (A.y1 - A.y0) * 0.2), (under.length ? Math.min(...under.map((q) => q.y0)) : this.H) - 70);
    g.save(); g.globalAlpha = a; g.translate(cx, cy); g.scale(k, k); g.rotate(-0.025);
    g.font = FONT.display(56); const w1 = g.measureText(sl.site.n).width;
    g.font = FONT.mono(13, 600); const w2 = g.measureText(sl.site.sub).width;
    const w = Math.max(w1, w2) + 44, h = 92;
    box(g, -w / 2, -h / 2, w, h, INK.metro, 4, 8);
    g.fillStyle = INK.asphalt; g.textAlign = 'center'; g.textBaseline = 'alphabetic';
    g.font = FONT.display(56); g.fillText(sl.site.n, 0, 12);
    g.font = FONT.mono(13, 600); g.fillText(sl.site.sub, 0, 34);
    g.restore();
  }

  /** Boxed labels on the sheet, then signposts at the edge to the places round it. */
  private labels(s: number, X: (x: number) => number, Y: (y: number) => number) {
    const D = this.D!, g = this.g, [cx, cy] = D.centre, R = D.rings, A = this.area();
    const taken: Rect[] = [...this.avoid()];
    const clear = (q: Rect) => !taken.some((t) => q.x0 < t.x1 && q.x1 > t.x0 && q.y0 < t.y1 && q.y1 > t.y0);
    const tagAt = (lines: string[], x: number, y: number, o: { metro?: boolean; size?: number; fill?: string; center?: boolean } = {}) => {
      const size = o.size ?? 17, lh = size + 2, icon = o.metro ? 24 : 0;
      g.font = FONT.display(size);
      const tw = Math.max(...lines.map((l) => g.measureText(l).width)), w = tw + 18 + icon, h = lines.length * lh + 10;
      const x0 = o.center ? x - w / 2 : x, y0 = o.center ? y - h / 2 : y;
      const q = { x0, y0, x1: x0 + w + 4, y1: y0 + h + 4 };
      if (!clear(q) || x0 < A.x0 || y0 < 0 || x0 + w > this.W || y0 + h > this.H) return false;
      taken.push(q);
      box(g, x0, y0, w, h, o.fill ?? INK.newsprint, 2, 3);
      if (o.metro) { // the M roundel
        const mx = x0 + 15, my = y0 + h / 2;
        g.beginPath(); g.arc(mx, my, 9, 0, Math.PI * 2); g.fillStyle = INK.asphalt; g.fill();
        g.fillStyle = INK.newsprint; g.font = FONT.display(14); g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText('M', mx, my + 1);
      }
      g.font = FONT.display(size); g.fillStyle = INK.asphalt; g.textAlign = 'left'; g.textBaseline = 'middle';
      lines.forEach((l, i) => g.fillText(l, x0 + 9 + icon, y0 + 5 + lh * i + lh / 2 + 1));
      return true;
    };
    tagAt(['CONNAUGHT', 'PLACE'], X(cx), Y(cy) - 6, { center: true, size: 20 });
    const on = (r: number, deg: number) => [X(cx + Math.cos((deg * Math.PI) / 180) * r), Y(cy + Math.sin((deg * Math.PI) / 180) * r)];
    let [x, y] = on(R.outer, 196); tagAt(['OUTER CIRCLE'], x, y, { center: true });
    [x, y] = on(R.inner + 4, 28); tagAt(['INNER CIRCLE'], x, y, { center: true });
    for (const st of D.stations) { // stations by name, Rajiv Chowk first
      const sx = X(st.x), sy = Y(st.y);
      tagAt([`${st.n.toUpperCase()} METRO`], sx - 40, sy - 58, { metro: true }) || tagAt([`${st.n.toUpperCase()} METRO`], sx + 18, sy - 14, { metro: true });
    }
    // signposts: on the sheet where a place is in view, else at the area's edge in its direction, with its distance
    const ccx = X(cx), ccy = Y(cy), pad = 18;
    for (const p of D.places) {
      const px = X(p.x), py = Y(p.y), label = [p.n.toUpperCase(), `${p.km.toFixed(1)} KM`];
      const inView = px > A.x0 + 60 && px < A.x1 - 60 && py > 60 && py < this.H - 60;
      if (inView) { if (tagAt(label, px - 50, py - 26, { size: 16 })) { g.fillStyle = INK.asphalt; g.beginPath(); g.arc(px, py, 4, 0, Math.PI * 2); g.fill(); } continue; }
      const dx = px - ccx, dy = py - ccy, k = Math.min(Math.abs((dx > 0 ? A.x1 - pad - 150 - ccx : A.x0 + pad - ccx) / (dx || 1e-6)), Math.abs((dy > 0 ? A.y1 - pad - 60 - ccy : A.y0 + pad - ccy) / (dy || 1e-6)));
      const ex = ccx + dx * k, ey = ccy + dy * k;
      g.font = FONT.display(16);
      const tw = Math.max(g.measureText(label[0]).width, g.measureText(label[1]).width) + 50, th = 46;
      const q = { x0: ex, y0: ey, x1: ex + tw + 4, y1: ey + th + 4 };
      if (!clear(q)) continue;
      taken.push(q);
      box(g, ex, ey, tw, th, INK.newsprint, 2, 3);
      g.fillStyle = INK.asphalt; g.fillRect(ex + 34, ey + 6, 2, th - 12);
      g.save(); g.translate(ex + 17, ey + th / 2); g.rotate(Math.atan2(dy, dx)); // an arrow, shaft and head, that points the way
      g.fillRect(-11, -2, 13, 4); g.beginPath(); g.moveTo(12, 0); g.lineTo(1, -8); g.lineTo(1, 8); g.closePath(); g.fill(); g.restore();
      g.font = FONT.display(16); g.textAlign = 'left'; g.textBaseline = 'middle';
      g.fillText(label[0], ex + 42, ey + 15); g.fillText(label[1], ex + 42, ey + 32);
    }
  }

  /** The minimap beside the legend: the city's highways and metro, and where this sheet is in it. */
  private drawMinimap() {
    const c = this.minimap, M = this.D?.minimap;
    if (!c || !M) return;
    const r = c.getBoundingClientRect(), d = Math.min(2, devicePixelRatio || 1);
    c.width = Math.round(r.width * d); c.height = Math.round(r.height * d);
    const g = c.getContext('2d')!, k = Math.min(r.width / M.w, r.height / M.h) * 0.92;
    g.setTransform(d, 0, 0, d, 0, 0);
    g.fillStyle = INK.asphalt; g.fillRect(0, 0, r.width, r.height);
    g.translate((r.width - M.w * k) / 2, (r.height - M.h * k) / 2); g.scale(k, k);
    const line = (l: number[]) => { g.beginPath(); g.moveTo(l[0], l[1]); for (let i = 2; i < l.length; i += 2) g.lineTo(l[i], l[i + 1]); g.stroke(); };
    g.lineCap = 'round'; g.lineJoin = 'round';
    g.strokeStyle = 'rgba(236,224,200,.55)'; g.lineWidth = 1.2 / k; for (const l of M.roads) line(l);
    g.strokeStyle = 'rgba(236,224,200,.3)'; g.lineWidth = 1 / k; g.setLineDash([3 / k, 2 / k]); for (const l of M.metro) line(l); g.setLineDash([]);
    const [a, b, e, f] = M.cut, pad = 6 / k;
    g.strokeStyle = INK.metro; g.lineWidth = 2 / k; g.strokeRect(Math.min(a, e) - pad, Math.min(b, f) - pad, Math.abs(e - a) + 2 * pad, Math.abs(f - b) + 2 * pad);
  }
}

/** The worn-print texture: a tile of cream specks (they knock holes in the ink) and ink specks (grime on the paper). */
let wearTile: HTMLCanvasElement | null = null;
function wear(): HTMLCanvasElement {
  if (wearTile) return wearTile;
  const c = document.createElement('canvas'), n = 256; c.width = c.height = n;
  const g = c.getContext('2d')!;
  let s = 7; const rnd = () => { s = (s * 16807) % 2147483647; return s / 2147483647; };
  for (let i = 0; i < 1400; i++) { g.fillStyle = `rgba(236,224,200,${0.25 + rnd() * 0.45})`; const r = 0.4 + rnd() * 1.3; g.fillRect(rnd() * n, rnd() * n, r, r); }
  for (let i = 0; i < 700; i++) { g.fillStyle = `rgba(14,12,9,${0.12 + rnd() * 0.3})`; const r = 0.4 + rnd() * 1.1; g.fillRect(rnd() * n, rnd() * n, r, r); }
  g.strokeStyle = 'rgba(236,224,200,.35)'; g.lineWidth = 0.7;
  for (let i = 0; i < 6; i++) { const x = rnd() * n, y = rnd() * n, a = rnd() * Math.PI; g.beginPath(); g.moveTo(x, y); g.lineTo(x + Math.cos(a) * 30, y + Math.sin(a) * 30); g.stroke(); }
  return (wearTile = c);
}

// ------------------------------------------------------------------ the dockets beside the pamphlet

/** What /status says (src/server/lobby.ts stats()), or null when the server can't be reached. */
export interface CityStatus { players: number; waiting?: number; lobbySize?: number; rooms?: number; phase?: string }

/** The city status docket: real numbers from /status, dashes when there are none. Nothing here is ever made up. */
export function cityStatus(s: CityStatus | null) {
  const $ = (id: string) => document.getElementById(id);
  const set = (id: string, v: string) => { const e = $(id); if (e) e.textContent = v; };
  const size = s?.lobbySize || 5, wait = s?.waiting ?? 0;
  set('city-on', s ? String(s.players) : '—');
  set('city-wait', s ? `${Math.min(wait, size)}/${size}` : '—');
  set('city-rooms', s ? String(s.rooms ?? 1) : '—');
  set('city-phase', s?.phase ? s.phase.toUpperCase() : '—');
  set('city-when', s ? 'LIVE' : 'NO SIGNAL');
  const bar = $('city-wait-bar'); if (bar) bar.style.width = `${s ? Math.min(100, (wait / size) * 100) : 0}%`;
}

/** The Section 144 notice: "Understood" stamps it NOTED, and it stays noted on this browser. */
export function section144(el: HTMLElement | null, store: { get(k: string, d: string): string; set(k: string, v: string): void }) {
  if (!el) return;
  const btn = el.querySelector<HTMLButtonElement>('#s144-ok');
  if (store.get('dharna_s144', '0') === '1') el.classList.add('noted');
  if (btn) btn.onclick = () => { el.classList.add('noted'); store.set('dharna_s144', '1'); };
}
